import { useEffect, useState } from "react"
import { motion } from "framer-motion"
/*
  The TYPE, not an icon. This read `import { Video as LucideIcon }` — the Video
  icon COMPONENT aliased to the name of a type, then used in a type position on
  `NavItem.icon`. It type-checked as an error and described the wrong thing:
  any icon is valid here, not specifically Video.
*/
import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"

interface NavItem {
  name: string
  page: 'home' | 'booking' | 'events' | 'membership' | 'contact'
  icon: LucideIcon
}

interface NavBarProps {
  items: NavItem[]
  className?: string
  currentPage: string
  onNavigate: (page: 'home' | 'booking' | 'events' | 'membership' | 'contact' | 'impressum' | 'datenschutz' | 'buchungsbedingungen' | 'cookie-einstellungen' | 'paypal-return' | 'paypal-cancel' | 'success' | 'error' | 'cancel-free') => void
}

export function NavBar({ items, className, currentPage, onNavigate }: NavBarProps) {
  const [activeTab, setActiveTab] = useState(currentPage)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    setActiveTab(currentPage)
  }, [currentPage])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const bookingItem = items.find((i) => i.page === 'booking')

  const handleItemClick = (item: NavItem) => {
    setActiveTab(item.page)
    window.history.pushState({}, '', `/${item.page === 'home' ? '' : item.page}`)
    onNavigate(item.page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div
      className={cn(
        "hidden sm:block fixed top-0 left-0 right-0 z-40 transition-all duration-300",
        scrolled ? "py-2" : "py-4",
        className
      )}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <button
          onClick={() => handleItemClick(items[0])}
          className="flex items-center gap-2 group svh-focus"
        >
          <img src="/photos/Image.jpeg" alt="SV Heinersreuth Logo" className="h-[64px] w-[64px] object-contain rounded-full" />
          <div className="hidden md:block">
            <span className="text-sm font-semibold text-gray-900 leading-none">SV Heinersreuth</span>
            <span className="block text-xs text-gray-500 leading-none mt-0.5">Tennis & Padel</span>
          </div>
        </button>

        <div
          className={cn(
            "flex items-center gap-1 py-1.5 px-2 rounded-full transition-all duration-300",
            scrolled
              ? "bg-white/95 border border-gray-200/80 backdrop-blur-xl shadow-lg"
              : "bg-white/90 border border-gray-200/60 backdrop-blur-md shadow-md"
          )}
        >
          {items.map((item) => {
            const isActive = activeTab === item.page

            return (
              <button
                key={item.name}
                onClick={() => handleItemClick(item)}
                aria-label={item.name}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "relative cursor-pointer text-[14px] font-semibold px-3 py-2 sm:px-4 rounded-full transition-colors duration-200 whitespace-nowrap svh-focus",
                  isActive
                    ? "text-[color:var(--svh-crest)]"
                    : "text-[color:var(--svh-ink-2)] hover:text-[color:var(--svh-ink)]"
                )}
              >
                {isActive && (
                  <motion.div
                    layoutId="nav-active-pill"
                    className="absolute inset-0 rounded-full"
                    style={{ background: 'var(--svh-crest-wash)' }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10 hidden lg:inline">{item.name}</span>
                <span className="relative z-10 lg:hidden">
                  <item.icon size={16} strokeWidth={2} />
                </span>
              </button>
            )
          })}
        </div>

        {/* Primary CTA — reuses the exact same navigation action as the "Buchen" nav item. */}
        {bookingItem ? (
          <button
            onClick={() => handleItemClick(bookingItem)}
            className="hidden md:inline-flex whitespace-nowrap svh-btn svh-btn--primary svh-btn--sm svh-focus"
          >
            Jetzt buchen
          </button>
        ) : (
          <div className="hidden md:block w-32" />
        )}
      </div>
    </div>
  )
}
